/* eslint-disable react/prop-types */
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@egaranti/components";
import { zodResolver } from "@hookform/resolvers/zod";

import { useCallback, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import extendedWarrantyService from "@/services/extendedWarrantyService";
import productService from "@/services/productService";

import ConfirmationDialog from "./confirmationDialog";
import SearchSelect from "./searchSelect";

import * as z from "zod";

/**
 * EPS-4970 revize — "Satış Ekle" ikinci adımı: ürün detayı + müşteri bilgileri.
 *
 * Alanlar: Marka, Ürün Adı, ardından Ad, Soyad, TCKN/VKN, Telefon, E-posta,
 * Fatura Tarihi, Fatura Numarası.
 *
 * <b>Marka ve Ürün Adı neden burada:</b> ilk adım yalnızca plan eşleştirmesi için
 * gerekeni topluyor (kategori + fiyat). Ürün, satış tamamlanırken bu iki alanla
 * çözülüyor/oluşturuluyor (product-api {@code /merchant-products/for-sale}).
 * İkisinde de listede olmayan değer yazılıp eklenebilir; mevcut ürün seçilirse id'si
 * taşınır ve yeni ürün açılmaz.
 *
 * <b>Telefon ya da e-posta:</b> ikisinden en az biri zorunlu (eskiden ikisi de
 * zorunluydu). Aynı kural bff'te de var (SellerCompleteBaseRequest#isContactProvided);
 * burada da doğruluyoruz ki kullanıcı hatayı alan üstünde görsün.
 *
 * <b>Fatura tarihi = garanti başlangıç tarihi.</b> Varsayılan bugün; ileri tarih
 * kabul edilmiyor (henüz kesilmemiş faturayla garanti başlatılamaz).
 *
 * <b>Satışı Yapan Personel (EPS-5097):</b> zorunlu; `extended_warranty.personal_name`'e
 * yazılır ve Personel Performansı raporu bu alanı gruplar. Daha önce girilmiş isimler
 * önerilir, listede yoksa "+" ile yeni ad eklenir — aynı kişinin farklı yazımlarla
 * raporda iki ayrı personel gibi görünmemesi için. Karakter kuralı bff ile aynı: rapor
 * satırları ':' ile birleştirildiği için yalnızca harf, boşluk, nokta, kesme işareti ve
 * tire.
 *
 * <b>İki buton:</b> "Taslak Olarak Kaydet" (doğrulama yok, yarım veri kaydedilir) ve
 * "Satışı Tamamla" (tam doğrulama + onay diyaloğu). Eskiden taslak 2 saniye debounce ile
 * kendiliğinden yazılıyordu; bayi neyin kaydedildiğini göremediği için buton tercih edildi.
 */
const onlyDigits = (value) => (value || "").replace(/\D/g, "");

const todayAsInputValue = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60 * 1000;
  // toISOString UTC'ye çeviriyor; yerel saatle gün kayması olmasın diye offset düşülüyor.
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
};

const PRODUCT_SEARCH_SIZE = 20;

const SALES_PERSONNEL_MAX_LENGTH = 100;
// En az iki harf; harf, birleşik aksan, boşluk, nokta, kesme işaretleri ve tire.
// bff SellerCompleteBaseRequest#salesPersonnelName ile aynı kural.
const SALES_PERSONNEL_PATTERN = /^(?=(?:[^\p{L}]*\p{L}){2})[\p{L}\p{M} .'’ʻ-]+$/u;

// Raporda tek personel olarak gruplansın diye ara boşluklar teke indirilir.
const normalizePersonnelName = (value) =>
  (value || "").trim().replace(/\s+/g, " ");

const SALE_INVOICE_ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const SALE_INVOICE_ALLOWED_EXTENSIONS = ["pdf", "jpg", "jpeg", "png"];
const SALE_INVOICE_MAX_BYTES = 10 * 1024 * 1024;
const SALE_INVOICE_ACCEPT = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

const buildFormSchema = (t) =>
  z
    .object({
      brandName: z
        .string()
        .trim()
        .min(1, t("extendedWarranty.add.brandRequired", "Marka giriniz")),
      productName: z
        .string()
        .trim()
        .min(1, t("extendedWarranty.add.productNameRequired", "Ürün adı giriniz")),
      firstName: z.string().trim().min(2, {
        message: t(
          "extendedWarranty.add.customer.firstNameMin",
          "Müşteri adı en az 2 karakter olmalıdır.",
        ),
      }),
      lastName: z.string().trim().min(2, {
        message: t(
          "extendedWarranty.add.customer.lastNameMin",
          "Müşteri soyadı en az 2 karakter olmalıdır.",
        ),
      }),
      // TCKN 11, VKN 10, JShShIR 14, STIR 9 hane. Tek alanda hepsi kabul edilir;
      // hangisi olduğu hane sayısından anlaşılır ve BE'ye olduğu gibi iletilir.
      // Dil koşulu yok: aktif arayüz dili ne olursa olsun (tr/uz) her iki
      // ülkenin numara formatı da kabul edilir.
      identificationNumber: z
        .string()
        .trim()
        .refine((value) => /^(?:[0-9]{9}|[0-9]{10}|[0-9]{11}|[0-9]{14})$/.test(value), {
          message: t(
            "extendedWarranty.add.customer.identificationInvalid",
            "Geçerli bir kimlik/vergi numarası giriniz.",
          ),
        }),
      // customer-api telefonu libphonenumber ile doğruluyor; geçersizse istisna
      // atıyor ve bff'te ErrorDecoder olmadığı için panele çıplak 500 dönüyordu.
      phone: z
        .string()
        .trim()
        .refine((value) => value === "" || /^5[0-9]{9}$/.test(onlyDigits(value)), {
          message: t(
            "extendedWarranty.add.customer.phoneInvalid",
            "Telefon numarası başında 0 olmadan 10 hane olmalıdır (örn. 5321234567).",
          ),
        }),
      email: z
        .string()
        .trim()
        .refine((value) => value === "" || z.string().email().safeParse(value).success, {
          message: t(
            "extendedWarranty.add.customer.emailInvalid",
            "Geçerli bir e-posta adresi giriniz.",
          ),
        }),
      invoiceDate: z
        .string()
        .min(1, {
          message: t(
            "extendedWarranty.add.customer.invoiceDateRequired",
            "Fatura tarihi giriniz.",
          ),
        })
        .refine((value) => value <= todayAsInputValue(), {
          message: t(
            "extendedWarranty.add.customer.invoiceDateFuture",
            "Fatura tarihi bugünden ileri olamaz.",
          ),
        }),
      invoiceNumber: z.string().trim().min(1, {
        message: t(
          "extendedWarranty.add.invoiceNumberRequired",
          "Fatura numarası giriniz",
        ),
      }),
      salesPersonnelName: z
        .string()
        .trim()
        .min(1, {
          message: t(
            "extendedWarranty.add.salesPersonnelRequired",
            "Satışı yapan personeli seçiniz ya da ekleyiniz.",
          ),
        })
        .max(SALES_PERSONNEL_MAX_LENGTH, {
          message: t(
            "extendedWarranty.add.salesPersonnelTooLong",
            "Personel adı en fazla 100 karakter olabilir.",
          ),
        })
        .refine((value) => SALES_PERSONNEL_PATTERN.test(value), {
          message: t(
            "extendedWarranty.add.salesPersonnelInvalid",
            "Personel adı yalnızca harf, boşluk, nokta, kesme işareti ve tire içerebilir.",
          ),
        }),
      saleInvoiceFile: z.any(),
    })
    .refine((data) => data.phone !== "" || data.email !== "", {
      path: ["phone"],
      message: t(
        "extendedWarranty.add.customer.contactRequired",
        "Telefon numarası ya da e-posta adresinden en az biri zorunludur.",
      ),
    });

function CustomerInfoForm({
  onSubmit,
  onSaveDraft,
  savingDraft,
  loading,
  initialValues,
  productInfo,
  // EPS-4971: seçilen planın tahsilat yöntemi yalnızca "Online Ödeme Linki" ise
  // bayi kendisi tahsil edemez — satış müşteri ödemesiyle oluşur. Taslak kaydetmek
  // yine mümkün; ödeme linki taslak üzerinden gönderilecek.
  sellerCollectionAllowed = true,
  // EPS-4971: planda online ödeme linki tanımlıysa müşteriye link gönderilebilir.
  paymentLinkAllowed = false,
  onSendPaymentLink,
  // EPS-4971: taslağın ödeme durumu (LINK_SENT | PAID | ...). Ödemesi alınmış ya da
  // linki yaşayan taslakta bayi elle satış tamamlamaya çalışmamalı.
  draftPaymentStatus = null,
}) {
  const { t } = useTranslation();
  const [showConfirmation, setShowConfirmation] = useState(false);
  // EPS-4971: link kanalı (analiz C1) — bayi satış anında seçiyor.
  const [paymentLinkChannel, setPaymentLinkChannel] = useState("SMS");
  // Ödeme linki gönderilmiş ya da ödeme alınmış taslakta elle tahsilat yolu kapalı:
  // BE de reddediyor (sale.requires.online.payment / mükerrer satış), buton açık
  // kalınca bayi anlamsız bir hata alıyor.
  const paymentAlreadyHandled =
    draftPaymentStatus === "PAID" || draftPaymentStatus === "LINK_SENT";
  const formSchema = useMemo(() => buildFormSchema(t), [t]);

  const [brands, setBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [personnelNames, setPersonnelNames] = useState([]);
  const [personnelLoading, setPersonnelLoading] = useState(false);

  // Mevcut ürün seçildiyse id'si burada tutulur; yeni ad yazılırsa temizlenir
  // (o durumda ürün satış tamamlanırken BE'de oluşturulur).
  const [selectedProduct, setSelectedProduct] = useState(
    initialValues?.selectedProduct || null,
  );

  const form = useForm({
    resolver: zodResolver(formSchema),
    // Taslaktan devam ederken bu bileşen ancak taslak yüklendikten sonra mount
    // olduğu için defaultValues yeterli; form.reset'e gerek yok.
    defaultValues: {
      brandName: initialValues?.brandName || "",
      productName: initialValues?.productName || "",
      firstName: initialValues?.firstName || "",
      lastName: initialValues?.lastName || "",
      identificationNumber: initialValues?.identificationNumber || "",
      phone: initialValues?.phone || "",
      email: initialValues?.email || "",
      invoiceDate: initialValues?.invoiceDate || todayAsInputValue(),
      invoiceNumber: initialValues?.invoiceNumber || "",
      salesPersonnelName: initialValues?.salesPersonnelName || "",
      saleInvoiceFile: initialValues?.saleInvoiceFile ?? null,
    },
  });

  const saleInvoiceInputRef = useRef(null);

  const keepUploadedSaleInvoice = (uploaded) =>
    form.setValue("saleInvoiceFile", uploaded);

  const handleSaleInvoiceSelect = (event, onChange) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const extension = file.name.split(".").pop().toLowerCase();
    const typeAccepted =
      !file.type || SALE_INVOICE_ALLOWED_TYPES.includes(file.type);
    if (!SALE_INVOICE_ALLOWED_EXTENSIONS.includes(extension) || !typeAccepted) {
      form.setError("saleInvoiceFile", {
        type: "manual",
        message: t(
          "extendedWarranty.add.saleInvoiceTypeError",
          "Yalnızca PDF, JPG veya PNG dosyası yükleyebilirsiniz.",
        ),
      });
      return;
    }
    if (file.size > SALE_INVOICE_MAX_BYTES) {
      form.setError("saleInvoiceFile", {
        type: "manual",
        message: t(
          "extendedWarranty.add.saleInvoiceSizeError",
          "Dosya boyutu en fazla 10 MB olabilir.",
        ),
      });
      return;
    }
    form.clearErrors("saleInvoiceFile");
    onChange({ file, name: file.name });
  };

  const searchBrands = useCallback((query) => {
    setBrandsLoading(true);
    productService
      .autocompleteBrands(query)
      .then((list) => setBrands(list || []))
      .catch(() => setBrands([]))
      .finally(() => setBrandsLoading(false));
  }, []);

  // Ürün araması ilk adımda seçilen kategoriye daraltılır — merchant'ın tüm kataloğunu
  // taramak yerine o kategorideki ürünler önerilir.
  const searchProducts = useCallback(
    (query) => {
      if (!productInfo?.mainCategoryId) return;
      setProductsLoading(true);
      productService
        .getProducts({
          page: 1,
          size: PRODUCT_SEARCH_SIZE,
          mainCategoryId: productInfo.mainCategoryId,
          categoryId: productInfo.categoryId || undefined,
          query: query || undefined,
        })
        .then((data) => setProducts(data?.content || []))
        .catch(() => setProducts([]))
        .finally(() => setProductsLoading(false));
    },
    [productInfo?.mainCategoryId, productInfo?.categoryId],
  );

  const searchPersonnel = useCallback((query) => {
    setPersonnelLoading(true);
    extendedWarrantyService
      .searchSalesPersonnel(query)
      .then((list) => setPersonnelNames(list || []))
      .catch(() => setPersonnelNames([]))
      .finally(() => setPersonnelLoading(false));
  }, []);

  const personnelItems = useMemo(
    () => personnelNames.map((name) => ({ key: name, name })),
    [personnelNames],
  );

  const brandItems = useMemo(
    () => brands.map((brand) => ({ key: brand.name, name: brand.name })),
    [brands],
  );

  const productItems = useMemo(
    () =>
      products.map((product) => ({
        key: product.id,
        name: product.name,
        description: product.brand || product.brandName,
        product,
      })),
    [products],
  );

  const withProduct = (values) => ({
    ...values,
    brandName: (values.brandName || "").trim(),
    productName: (values.productName || "").trim(),
    salesPersonnelName: normalizePersonnelName(values.salesPersonnelName),
    // Mevcut ürün seçildiyse id'si taşınır; yeni ürün açılmaz.
    selectedProduct,
  });

  // Taslak doğrulamadan geçmez: form.handleSubmit yerine getValues ile ham değerler
  // gönderilir, böylece bayi hiçbir alanı doldurmadan da satışı park edebilir.
  const handleSaveDraft = () =>
    onSaveDraft?.(withProduct(form.getValues()), keepUploadedSaleInvoice);

  const handleConfirm = () => {
    setShowConfirmation(false);
    const values = withProduct(form.getValues());
    const submitValues = {
      ...values,
      // Numarayı rakamlara indirgeyerek gönder; backend E164'e kendisi çeviriyor.
      phone: onlyDigits(values.phone),
    };
    onSubmit(submitValues, keepUploadedSaleInvoice);
  };

  return (
    <section className="formBox">
      <h2 className="text-lg font-medium text-[#101828]">
        {t(
          "extendedWarranty.add.productAndCustomerInfoTitle",
          "Ürün ve Müşteri Bilgileri",
        )}
      </h2>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(() => setShowConfirmation(true))}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="brandName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("extendedWarranty.add.brandLabel", "Marka")} *
                  </FormLabel>
                  <FormControl>
                    <SearchSelect
                      value={field.value}
                      items={brandItems}
                      loading={brandsLoading}
                      allowCreate
                      onSearch={searchBrands}
                      placeholder={t(
                        "extendedWarranty.add.brandPlaceholder",
                        "Marka arayın ya da yazıp ekleyin",
                      )}
                      emptyHint={t(
                        "productsPage.typeToCreate",
                        "Eklemek için bir ad yazın.",
                      )}
                      onSelect={(item) => field.onChange(item.name)}
                      onCreate={(name) => field.onChange(name)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="productName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("extendedWarranty.add.productNameLabel", "Ürün Adı")} *
                  </FormLabel>
                  <FormControl>
                    <SearchSelect
                      value={field.value}
                      items={productItems}
                      loading={productsLoading}
                      allowCreate
                      onSearch={searchProducts}
                      placeholder={t(
                        "extendedWarranty.add.productNamePlaceholder",
                        "Ürün arayın ya da yazıp ekleyin",
                      )}
                      emptyHint={t(
                        "productsPage.typeToCreate",
                        "Eklemek için bir ad yazın.",
                      )}
                      onSelect={(item) => {
                        field.onChange(item.name);
                        setSelectedProduct(item.product);
                        // Mevcut ürünün markası varsa forma taşı — kullanıcı
                        // markayı ayrıca düzeltmek zorunda kalmasın.
                        const existingBrand =
                          item.product?.brand || item.product?.brandName;
                        if (existingBrand) {
                          form.setValue("brandName", existingBrand);
                        }
                      }}
                      onCreate={(name) => {
                        field.onChange(name);
                        setSelectedProduct(null);
                      }}
                    />
                  </FormControl>
                  {selectedProduct && (
                    <FormDescription>
                      {t(
                        "extendedWarranty.add.existingProductSelected",
                        "Mevcut ürün seçildi (Ürün Kodu: {{code}})",
                        { code: selectedProduct.productCode || "-" },
                      )}
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("extendedWarranty.add.customer.firstNameLabel", "Müşteri Adı")}{" "}
                    *
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(
                      "extendedWarranty.add.customer.lastNameLabel",
                      "Müşteri Soyadı",
                    )}{" "}
                    *
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="identificationNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(
                      "extendedWarranty.add.customer.identificationLabel",
                      "Müşteri TCKN / VKN",
                    )}{" "}
                    *
                  </FormLabel>
                  <FormControl>
                    <Input inputMode="numeric" maxLength={14} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("extendedWarranty.add.phoneLabel", "Telefon Numarası")}
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="5321234567" {...field} />
                  </FormControl>
                  <FormDescription>
                    {t(
                      "extendedWarranty.add.customer.contactHint",
                      "Telefon ya da e-postadan en az biri zorunludur.",
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("extendedWarranty.add.emailLabel", "E-Mail")}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="invoiceDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("extendedWarranty.add.invoiceDateLabel", "Fatura Tarihi")} *
                  </FormLabel>
                  <FormControl>
                    <Input type="date" max={todayAsInputValue()} {...field} />
                  </FormControl>
                  <FormDescription>
                    {t(
                      "extendedWarranty.add.invoiceDateDescription",
                      "Garanti başlangıç tarihi olarak kullanılır.",
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="invoiceNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("extendedWarranty.add.invoiceNumberLabel", "Fatura Numarası")}{" "}
                    *
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="FA112234394943" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="saleInvoiceFile"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("extendedWarranty.add.saleInvoiceLabel", "Fatura Görseli Ekle")}
                  </FormLabel>
                  {field.value ? (
                    <div className="flex min-h-10 flex-wrap items-center gap-3 rounded-lg border border-[#D0D5DD] px-3 py-2 text-sm">
                      {field.value.url ? (
                        <a
                          href={field.value.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="break-all text-blue-600 underline hover:text-blue-800"
                        >
                          {field.value.name || field.value.path}
                        </a>
                      ) : (
                        <span className="break-all text-[#101828]">
                          {field.value.name}
                        </span>
                      )}
                      {!paymentAlreadyHandled && (
                        <div className="ml-auto flex items-center gap-2">
                          <input
                            ref={saleInvoiceInputRef}
                            type="file"
                            accept={SALE_INVOICE_ACCEPT}
                            className="hidden"
                            onChange={(event) =>
                              handleSaleInvoiceSelect(event, field.onChange)
                            }
                          />
                          <Button
                            type="button"
                            variant="secondaryGray"
                            size="sm"
                            onClick={() => saleInvoiceInputRef.current?.click()}
                          >
                            {t("extendedWarranty.add.saleInvoiceChange", "Değiştir")}
                          </Button>
                          <Button
                            type="button"
                            variant="secondaryGray"
                            size="sm"
                            onClick={() => {
                              form.clearErrors("saleInvoiceFile");
                              field.onChange(null);
                            }}
                          >
                            {t("extendedWarranty.add.saleInvoiceRemove", "Kaldır")}
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <FormControl>
                      <Input
                        type="file"
                        accept={SALE_INVOICE_ACCEPT}
                        disabled={paymentAlreadyHandled}
                        onChange={(event) =>
                          handleSaleInvoiceSelect(event, field.onChange)
                        }
                      />
                    </FormControl>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="salesPersonnelName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(
                      "extendedWarranty.add.salesPersonnelLabel",
                      "Satışı Yapan Personel",
                    )}{" "}
                    *
                  </FormLabel>
                  <FormControl>
                    <SearchSelect
                      value={field.value}
                      items={personnelItems}
                      loading={personnelLoading}
                      allowCreate
                      onSearch={searchPersonnel}
                      placeholder={t(
                        "extendedWarranty.add.salesPersonnelPlaceholder",
                        "Personel arayın ya da yazıp ekleyin",
                      )}
                      emptyHint={t(
                        "productsPage.typeToCreate",
                        "Eklemek için bir ad yazın.",
                      )}
                      onSelect={(item) => field.onChange(item.name)}
                      onCreate={(name) =>
                        field.onChange(normalizePersonnelName(name))
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {paymentAlreadyHandled && (
            <p className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">
              {draftPaymentStatus === "PAID"
                ? t(
                    "extendedWarranty.add.paymentAlreadyReceived",
                    "Bu satışın ödemesi müşteri tarafından tamamlandı; satış kaydı oluşturuluyor. Elle tamamlamayın.",
                  )
                : t(
                    "extendedWarranty.add.paymentLinkPending",
                    "Müşteriye ödeme linki gönderildi ve link hâlâ geçerli. Ödeme tamamlanınca satış otomatik oluşacak.",
                  )}
            </p>
          )}

          {!sellerCollectionAllowed && (
            <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
              {t(
                "extendedWarranty.add.onlyOnlineCollection",
                "Bu paketin tahsilat yöntemi yalnızca online ödeme linki. Satışı bayi olarak tamamlayamazsınız; müşteriye ödeme linki gönderin.",
              )}
            </p>
          )}

          {/* EPS-4971: Planda online ödeme linki tanımlıysa kanal seçimi ve link
             gönderme aksiyonu görünür. İkisi de tanımlıysa bayi hangisini
             kullanacağını kendisi seçiyor (analiz F4). */}
          {paymentLinkAllowed && (
            <div className="flex flex-col gap-2 rounded-lg bg-[#F5F8FF] p-3 text-sm">
              <span className="font-medium text-[#101828]">
                {t(
                  "extendedWarranty.add.paymentLinkChannel",
                  "Ödeme linki hangi kanaldan gönderilsin?",
                )}
              </span>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={paymentLinkChannel === "SMS"}
                    onChange={() => setPaymentLinkChannel("SMS")}
                  />
                  {t("extendedWarranty.add.channelSms", "SMS")}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={paymentLinkChannel === "EMAIL"}
                    onChange={() => setPaymentLinkChannel("EMAIL")}
                  />
                  {t("extendedWarranty.add.channelEmail", "E-posta")}
                </label>
              </div>
            </div>
          )}

          <div className="flex flex-col justify-end gap-3 md:flex-row">
            <Button
              type="button"
              variant="secondaryGray"
              onClick={handleSaveDraft}
              disabled={loading || savingDraft}
            >
              {t("extendedWarranty.add.saveAsDraft", "Taslak Olarak Kaydet")}
            </Button>
            {paymentLinkAllowed && (
              <Button
                type="button"
                variant="secondaryColor"
                disabled={loading || savingDraft}
                onClick={() =>
                  form.handleSubmit((values) =>
                    onSendPaymentLink?.(
                      withProduct(values),
                      paymentLinkChannel,
                      keepUploadedSaleInvoice,
                    ),
                  )()
                }
              >
                {t(
                  "extendedWarranty.add.sendPaymentLink",
                  "Ödeme Linki Gönder",
                )}
              </Button>
            )}
            <Button
              type="submit"
              disabled={
                loading ||
                savingDraft ||
                !sellerCollectionAllowed ||
                paymentAlreadyHandled
              }
            >
              {t(
                "extendedWarranty.add.completeSaleStoreCollection",
                "Satışı Tamamla (Mağazadan Tahsilat)",
              )}
            </Button>
          </div>
        </form>
      </Form>

      <ConfirmationDialog
        open={showConfirmation}
        onOpenChange={setShowConfirmation}
        onConfirm={handleConfirm}
      />
    </section>
  );
}

export default CustomerInfoForm;
